/**
 * AWS CloudWatch RUM telemetry backend
 *
 * Wraps the AWS RUM SDK (aws-rum-web) into the TelemetryBackend interface.
 * Maps logger operations to the closest RUM equivalent:
 *
 * - trace/debug/info/warn → recordEvent with severity metadata
 * - trackException       → recordError + metadata event
 * - trackEvent           → recordEvent
 * - trackDependency      → no-op (RUM auto-captures HTTP via telemetries config)
 * - trackPageView        → recordPageView
 * - setUser              → addSessionAttributes
 * - clearUser            → no-op (RUM sessions don't support clearing)
 * - flush                → dispatch
 */

import type { AwsRum, AwsRumConfig } from "aws-rum-web";
import type { TelemetryBackend } from "./types";
import type { DependencyData } from "../types";

import { LogLevel } from "../types";

const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: "DEBUG",
  [LogLevel.INFO]: "INFO",
  [LogLevel.WARN]: "WARN",
  [LogLevel.ERROR]: "ERROR",
  [LogLevel.CRITICAL]: "CRITICAL",
};

let rumInstance: AwsRum | null = null;
let initialised = false;

export interface AwsRumBackendConfig {
  applicationId: string;
  applicationVersion: string;
  region: string;
  config?: Partial<AwsRumConfig>;
}

export function getAwsRum(): AwsRum | null {
  return rumInstance;
}

export function isAwsRumInitialised(): boolean {
  return initialised;
}

/**
 * Initialise AWS CloudWatch RUM. Idempotent — safe to call multiple times.
 * Returns the AwsRum instance or null if initialisation is skipped/fails.
 */
export async function initialiseAwsRum(backendConfig: AwsRumBackendConfig): Promise<AwsRum | null> {
  if (initialised) return rumInstance;

  const { applicationId, applicationVersion, region, config } = backendConfig;

  if (!applicationId) {
    initialised = true;

    return null;
  }

  try {
    const { AwsRum } = await import("aws-rum-web");
    const rumConfig: AwsRumConfig = {
      sessionSampleRate: 1,
      endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
      telemetries: ["http", "performance", "errors"],
      allowCookies: true,
      enableXRay: false,
      useBeacon: true,
      ...config,
    };

    rumInstance = new AwsRum(applicationId, applicationVersion, region, rumConfig);
    initialised = true;

    return rumInstance;
  } catch {
    initialised = true;

    return null;
  }
}

/** Reset initialisation state (for testing only) */
export function _resetAwsRum(): void {
  rumInstance = null;
  initialised = false;
}

export function createAwsRumBackend(): TelemetryBackend {
  return {
    name: "AwsRum",

    trace(level, message, properties) {
      const rum = getAwsRum();

      if (!rum) return;

      // RUM has no trace API — send as custom event with severity metadata
      rum.recordEvent(`log_${LOG_LEVEL_NAMES[level].toLowerCase()}`, {
        message,
        severity: LOG_LEVEL_NAMES[level],
        ...properties,
      });
    },

    trackException(error, properties, componentStack) {
      const rum = getAwsRum();

      if (!rum) return;

      rum.recordError(error);

      // Also record metadata as a custom event for richer diagnostics
      if (properties || componentStack) {
        rum.recordEvent("exception_metadata", {
          errorMessage: error.message,
          ...properties,
          ...(componentStack ? { componentStack } : {}),
        });
      }
    },

    trackEvent(name, properties) {
      const rum = getAwsRum();

      if (!rum) return;

      rum.recordEvent(name, { ...properties });
    },

    trackDependency(_data: DependencyData) {
      // No-op: AWS RUM automatically captures HTTP telemetry
      // via the "http" entry in the telemetries config array.
    },

    trackPageView(name, uri) {
      const rum = getAwsRum();

      if (!rum) return;

      rum.recordPageView(uri ?? name);
    },

    setUser(user) {
      const rum = getAwsRum();

      if (!rum) return;

      rum.addSessionAttributes({
        userId: user.id,
        userEmail: user.email,
        userName: user.name,
        isEmbedded: String(user.isEmbedded),
        ...(user.tenant ? { tenant: user.tenant } : {}),
      });
    },

    clearUser() {
      // AWS RUM does not support clearing session attributes.
      // Session attributes persist for the session duration.
    },

    flush() {
      const rum = getAwsRum();

      if (!rum) return;

      rum.dispatch();
    },
  };
}
